import * as z from 'zod/mini';
import { attachmentSchema } from './attachment.ts';
import { checklistItemSchema, checklistSchema } from './checklist.ts';
import { idSchema, nodeTypeSchema, pathSchema, timestampSchema } from './common.ts';
import { connectionSchema } from './connection.ts';
import { contactSchema } from './contact.ts';
import { credentialStatusSchema } from './credentials.ts';
import { customTypeSchema } from './custom-type.ts';
import { noteSchema } from './note.ts';
import { photoSchema } from './photo.ts';
import { powerFeedSchema } from './power.ts';
import { propertySchema } from './property.ts';
import { recordSchema } from './record.ts';
import { roomTreeSchema } from './responses.ts';
import { vlanSchema } from './vlan.ts';

// The two reads an offline copy is kept up to date with.
//
// The manifest says what has moved since a moment, so a device coming back
// online can refresh only what changed instead of re-reading every property
// it keeps. The bundle is one property's whole record in a single reply, so
// "Keep offline" is one request rather than a walk of the tree.

/**
 * The rows a manifest reports by version. These are the tables that carry an
 * `updated_at`, which is exactly the set of things a write can send
 * `If-Match` for; everything else that hangs off a node (a note, a photo, a
 * file, a link, a power feed) is reported through `touched` instead.
 */
export const SYNC_NODE_TYPES = [
  'client',
  'property',
  'room',
  'rack',
  'item',
  'drop',
  'vlan',
  'contact',
  'checklist',
] as const;
export type SyncNodeType = (typeof SYNC_NODE_TYPES)[number];
export const syncNodeTypeSchema = z.enum(SYNC_NODE_TYPES);

export const syncEntrySchema = z.object({
  type: syncNodeTypeSchema,
  id: idSchema,
  updatedAt: timestampSchema,
});
export type SyncEntry = z.infer<typeof syncEntrySchema>;

/** A node a delete removed. Its subtree went with it and is not listed separately. */
export const syncRemovalSchema = z.object({
  type: nodeTypeSchema,
  id: idSchema,
  at: timestampSchema,
});
export type SyncRemoval = z.infer<typeof syncRemovalSchema>;

export const syncManifestSchema = z.object({
  /** The server's clock when it answered; the `since` of the next manifest. */
  at: timestampSchema,
  changed: z.array(syncEntrySchema),
  deleted: z.array(syncRemovalSchema),
  /**
   * Nodes something under them changed on without moving their own row: a
   * note, photo, file, link, or power feed added or removed. A kept copy
   * holding one of these refreshes its property in full.
   */
  touched: z.array(idSchema),
  /** More changed than the reply carries; ask again from `at` after acting on this. */
  truncated: z.boolean(),
});
export type SyncManifest = z.infer<typeof syncManifestSchema>;

/** One list with its steps, as the offline bundle carries it. */
export const offlineChecklistSchema = z.object({
  checklist: checklistSchema,
  items: z.array(checklistItemSchema),
});

/**
 * Everything needed to keep one property offline, in one reply: the tree,
 * the tables that hang off it, and the metadata of its photos and files —
 * never their bytes, which the client fetches separately for the ones it can
 * hold. It carries no device credential, no access code, and no token: those
 * leave the Worker only through their own reveal routes, and this route
 * never reads their tables.
 */
export const offlineBundleSchema = z.object({
  at: timestampSchema,
  property: propertySchema,
  path: pathSchema,
  rooms: z.array(roomTreeSchema),
  vlans: z.array(vlanSchema),
  records: z.optional(z.array(recordSchema)),
  customTypes: z.optional(z.array(customTypeSchema)),
  contacts: z.array(contactSchema),
  checklists: z.array(offlineChecklistSchema),
  notes: z.array(noteSchema),
  connections: z.array(connectionSchema),
  power: z.array(powerFeedSchema),
  photos: z.array(photoSchema),
  attachments: z.array(attachmentSchema),
  /**
   * Which devices have credentials stored and when each was last changed —
   * never a value, which leaves the Worker only through the reveal route.
   * The item page says "Stored, updated …" from exactly this, so a copy
   * without it would have to claim a device has no credentials when it has.
   */
  sealed: z.array(z.object({ itemId: idSchema, updatedAt: timestampSchema })),
  /** Whether the property has access codes sealed, and when they last changed; never a code. */
  access: credentialStatusSchema,
  /** Photos or files past the bundle's ceiling were left out of the lists above. */
  truncated: z.boolean(),
});
export type OfflineBundle = z.infer<typeof offlineBundleSchema>;
